import { Flex, Spinner, Text, Button } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import { logout, selectIsAuthenticated } from "@/entities/session";
import { useAppDispatch, useAppSelector } from "./appStore";
import {
  useLazyStaffMeQuery,
  useLazyStaffStoreAppDetailsQuery,
} from "@/shared/api";
import { convertServicesToEnvironment } from "@/shared/lib/environment";
import {
  refreshAccessToken,
  updateGlobalAPIBaseURLs,
} from "@/shared/api/axios";
import { useAuthRedirect } from "@/shared/hooks/useAuthRedirect";
import { getAppKey } from "@/shared/api/helpers";
import { ExitIcon } from "@radix-ui/react-icons";
import { appSession } from "@/shared/lib/storage";

export function ProtectedView() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const navigate = useNavigate();
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const { redirectToLogin } = useAuthRedirect();
  const { storeKey = "" } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [getStaffMe] = useLazyStaffMeQuery();

  const initializeEnvironment = async ({ storeKey }: { storeKey: string }) => {
    try {
      const appDetails = await getAppDetails({
        storeKey,
        appKey: getAppKey(),
      }).unwrap();

      await refreshAccessToken(storeKey);
      const services = convertServicesToEnvironment(
        appDetails?.result?.services
      );

      updateGlobalAPIBaseURLs(services);
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      redirectToLogin();
      setIsLoading(false);
    } else {
      if (storeKey) {
        Promise.all([
          initializeEnvironment({ storeKey }),
          getStaffMe(undefined, true).unwrap(),
        ]).catch((error) => {
          setError(error);
          setIsLoading(false);
        });
      } else {
        navigate("/stores");
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  return (
    <>
      {isLoading ? (
        <Flex className="h-dvh" gap="2" justify="center" align="center">
          <Spinner size="3" />
          <Text color="gray" size="2">
            Starting app...
          </Text>
        </Flex>
      ) : error ? (
        <ErrorView error={error} />
      ) : (
        <Outlet />
      )}
    </>
  );
}

function ErrorView({ error }: { error: any }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { redirectToLoginAfterLogout } = useAuthRedirect();

  return (
    <Flex className="h-dvh" gap="1" justify="center" align="center">
      {error?.status && (
        <Text color="gray" size="2">
          {error.status}
        </Text>
      )}
      <Text color="gray" size="2">
        {error?.data?.detail || "Unexpected error. Please try again."}
      </Text>

      <Button
        disabled={loading}
        className="absolute top-6 right-8"
        variant="ghost"
        color="gray"
        onClick={() => {
          setLoading(true);
          dispatch(logout())
            .unwrap()
            .then(() => {
              setLoading(false);
              appSession.removeItem();
              redirectToLoginAfterLogout();
            });
        }}
      >
        <ExitIcon /> Logout
      </Button>
    </Flex>
  );
}
